import { Options, Vue } from 'vue-class-component';
import { mapState } from 'vuex';
import {
  EntrancePlantDetailDTO,
  GateMessageDTO,
} from '@/../client-generator/generated-clients/plants';
import ListItemCheck from '@/views/components/ListItemCheck/ListItemCheck.vue';
import { SET_APP_LOADING } from '@/core/store/action-types';
import plantsClient from '@/core/services/plants';
import { PropType } from 'vue';

@Options({
  name: 'DialogAddEditMessage',
  data() {
    const today = new Date();
    const nextYear = new Date(today);
    nextYear.setFullYear(today.getFullYear() + 1);
  
    return {
      minDate: today,
      maxDate: nextYear,
      startDate: today,
      endDate: new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1)
    };
  },
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
    messageToEdit: {
      type: Object as PropType<GateMessageDTO>,
      required: false,
    },
  },
  computed: {
    ...mapState(['currentEntrancePlant']),
    minDurationUntilDate() {
      const nextDay = new Date(this.message.durationSince);
      nextDay.setDate(this.message.durationSince.getDate());
      return nextDay;
    }
  },
  watch: {
    isVisible: function (newValue) {
      this.isDialogVisible = newValue;
      if (this.isDialogVisible) {
        this.initMessage();
      }
    },
    'message.durationSince': function (newValue) {
      if (newValue > this.message.durationUntil) {
        this.message.durationUntil = this.nextValidDurationUntil(newValue);
      }
    },
  },
  methods: {
    resetMinutes(date:Date) {
      if (date) {
        date.setMinutes(0, 0, 0);
        return date;
      }
    }
  },
  components: { ListItemCheck },
  emits: ['hide-dialog', 'update-list'],
})
export default class DialogAddEditMessage extends Vue {
[x: string]: any;
  messageToEdit!: GateMessageDTO;
  currentEntrancePlant!: EntrancePlantDetailDTO;

  message: GateMessageDTO = new GateMessageDTO();
  messageMaxLength = 225;
  isVisible!: boolean;
  isDialogVisible = false;
  isEdit = false;

  get dialogTitle() {
    return this.isEdit
      ? this.$t('AppResourceEP.EditGateMessage')
      : this.$t('AppResourceEP.NewGateMessage');
  }

  get currentEntrancePlantGatesCount() {
    return this.currentEntrancePlant.gates?.length ?? 0;
  }

  get remainingChars() {
    if (!this.message.text) return this.messageMaxLength;
    return this.messageMaxLength - this.message.text.length;
  }

  nextValidDurationUntil(value: Date) {
    const nextValidDurationUntil = new Date(value);

    nextValidDurationUntil.setHours(value.getHours() + 1);

    return nextValidDurationUntil;
  }

  handleHideDialog() {
    this.isDialogVisible = false;
  }

  doHideDialog() {
    this.$emit('hide-dialog');
  }

  initMessage() {
    if (this.messageToEdit) {
      this.isEdit = true;
      this.message = new GateMessageDTO(this.messageToEdit);
    } else {
      this.isEdit = false;
      this.message = new GateMessageDTO({
        title: '',
        text: '',
        durationSince: new Date(),
        durationUntil: this.nextValidDurationUntil(new Date()),
        gateIds: [],
      });
      this.message.durationSince?.setHours(
        this.message.durationSince.getHours() + 1
      );
      this.message.durationSince?.setMinutes(0);
      this.message.durationUntil = this.nextValidDurationUntil(
        this.message.durationSince!
      );
    }
  }

  async handleButtonSubmitAction() {
    this.isDialogVisible = false;

    await this.saveGateMessage();

    this.$emit('update-list');
    this.$toast.add({
      severity: 'success',
      summary: this.isEdit ? this.$t('entrancePlant.gateMessageEdited') : this.$t('AppResourceEP.GateMessageBannerCreated'),
      closable: false,
      life: 3000,
    });
    const summary = this.$t('general.synced');
    setTimeout(() => {
      this.$toast.add({
        severity: 'success',
        summary: summary,
        closable: false,
        life: 3000,
      });
    }, 1500);
  }

  async saveGateMessage() {
    if (this.currentEntrancePlant.desktopId === undefined) return;
    this.$store.dispatch(SET_APP_LOADING, true);
    try {
      if (this.isEdit) {
        await plantsClient.gatemessagePUT(
          this.currentEntrancePlant.desktopId,
          this.message
        );
      } else {
        await plantsClient.gatemessagePOST(
          this.currentEntrancePlant.desktopId,
          this.message
        );
      }
    } catch (error) {
      console.log(error);
      setTimeout(() => {
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          closable: false,
          life: 3000,
        });
      }, 1500);
    } finally {
      this.$store.dispatch(SET_APP_LOADING, false);
    }
  }

  get saveButtonDisabled() {
    return !this.message.title || this.message.title.trim().length === 0;
  }
}
